import {
  BaseHTTPClient,
  BaseHTTPClientResponse,
  Query,
} from './baseHTTPClient';

export interface AlgodTokenHeader {
  'X-Algo-API-Token': string;
}

export interface IndexerTokenHeader {
  'X-Indexer-API-Token': string;
}

export interface KMDTokenHeader {
  'X-KMD-API-Token': string;
}

export interface CustomTokenHeader {
  [headerName: string]: string;
}

export type TokenHeader =
  | AlgodTokenHeader
  | IndexerTokenHeader
  | KMDTokenHeader
  | CustomTokenHeader;

export class URLTokenBaseHTTPClient implements BaseHTTPClient {
  private readonly baseURL: URL;
  private readonly tokenHeader: TokenHeader;

  constructor(
    tokenHeader: TokenHeader,
    baseServer: string,
    port?: string | number,
    private defaultHeaders: Record<string, any> = {}
  ) {
    // Append a trailing slash so we can use relative paths. Without the trailing
    // slash, the last path segment will be replaced by the relative path. See
    // usage in `addressWithPath`.
    const fixedBaseServer = baseServer.endsWith('/')
      ? baseServer
      : `${baseServer}/`;
    const baseServerURL = new URL(fixedBaseServer);
    if (typeof port !== 'undefined') {
      baseServerURL.port = port.toString();
    }

    if (baseServerURL.protocol.length === 0) {
      throw new Error('Invalid base server URL, protocol must be defined.');
    }

    this.baseURL = baseServerURL;
    this.tokenHeader = tokenHeader;
  }

  /**
   * Compute the URL for a path relative to the instance's address
   * @param relativePath - A path string
   * @returns A URL string
   */
  private addressWithPath(
    relativePath: string,
    query?: Record<string, string>
  ): string {
    let fixedRelativePath: string;
    if (relativePath.startsWith('./')) {
      fixedRelativePath = relativePath;
    } else if (relativePath.startsWith('/')) {
      fixedRelativePath = `.${relativePath}`;
    } else {
      fixedRelativePath = `./${relativePath}`;
    }
    const address = new URL(fixedRelativePath, this.baseURL);
    const url = address.toString();
    const search = String(new URLSearchParams(query));
    return search ? `${url}?${search}` : url;
  }

  private static async fetchToHTTPClientResponse(
    res
  ): Promise<BaseHTTPClientResponse> {
    return {
      body: new Uint8Array(await res.arrayBuffer()),
      status: res.status,
      headers: res.headers,
    };
  }

  async get(
    relativePath: string,
    query?: Query<string>,
    requestHeaders: Record<string, string> = {}
  ): Promise<BaseHTTPClientResponse> {
    const res = await fetch(this.addressWithPath(relativePath, query), {
      method: 'GET',
      headers: {
        ...this.tokenHeader,
        ...this.defaultHeaders,
        ...requestHeaders,
      },
    });

    return URLTokenBaseHTTPClient.fetchToHTTPClientResponse(res);
  }

  async post(
    relativePath: string,
    data: Uint8Array,
    query?: Query<string>,
    requestHeaders: Record<string, string> = {}
  ): Promise<BaseHTTPClientResponse> {
    const res = await fetch(this.addressWithPath(relativePath, query), {
      method: 'POST',
      headers: {
        ...this.tokenHeader,
        ...this.defaultHeaders,
        ...requestHeaders,
      },
      body: Buffer.from(data),
    });

    return URLTokenBaseHTTPClient.fetchToHTTPClientResponse(res);
  }

  async delete(
    relativePath: string,
    data: Uint8Array,
    query?: Query<string>,
    requestHeaders: Record<string, string> = {}
  ): Promise<BaseHTTPClientResponse> {
    const res = await fetch(this.addressWithPath(relativePath, query), {
      method: 'DELETE',
      headers: {
        ...this.tokenHeader,
        ...this.defaultHeaders,
        ...requestHeaders,
      },
      body: Buffer.from(data),
    });

    return URLTokenBaseHTTPClient.fetchToHTTPClientResponse(res);
  }
}
